/**
 * DonneesPage — UNE page pour importer, exporter et récupérer les modèles.
 *
 * ─── POURQUOI ELLE REMPLACE DEUX ÉCRANS ─────────────────────────────────────
 *
 * Il y avait « Import données » et « Export données », avec la MÊME liste de
 * référentiels et des boutons différents — plus, depuis le 29/07, un troisième
 * bloc pour le parc bâtimentaire. Henrico : « une seule page, import export et
 * modèle, ça évite d'avoir quarante menus ».
 *
 * Trois boutons par ligne, quatorze référentiels, un seul écran.
 *
 * ─── L'ORDRE EST L'INFORMATION PRINCIPALE ───────────────────────────────────
 *
 * Un fichier de clés téléchargé avant l'import des lieux ne propose aucun
 * bâtiment : la commune retape des noms à la main, et les doublons naissent là.
 * Les thèmes sont donc groupés par étape, dans l'ordre où il faut les faire.
 *
 * Le NUMÉRO d'étape porte l'information ; la couleur ne fait que renforcer. Un
 * daltonien ou un écran mal réglé perdrait un code purement chromatique, et ce
 * serait un manquement RGAA. Chaque ligne NOMME en plus ses dépendances —
 * « dépendant » tout seul ne dit pas de quoi.
 *
 * ─── AUCUN `<a href>` POUR TÉLÉCHARGER ──────────────────────────────────────
 *
 * Le jeton vit en mémoire (voir l'en-tête d'`api.ts`), pas dans un cookie. Un
 * lien ouvert par le navigateur n'envoie aucun en-tête `Authorization` : le
 * serveur répond 401 et l'utilisateur voit une page noire. C'est exactement ce
 * qui est arrivé le 30/07. Tout passe par `downloadFichier`.
 */

import { useEffect, useState } from 'react';
import { Card, Alert, Button, Badge, ModuleHeader } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { ImportApercu } from '@roronoa-collectivites/shared-types';
import {
  Database, Upload, Download, FileSpreadsheet, Info, ArrowRight,
  AlertTriangle, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';

interface ThemeLigne {
  theme: string; titre: string; desc: string;
  etape: number; dependances: string[];
  famille: 'parc' | 'legacy' | 'rh';
  modeleDispo: boolean;
}
interface Etape {
  numero: number; titre: string; sousTitre: string; couleur: string;
  themes: ThemeLigne[];
}

export function DonneesPage() {
  const [etapes, setEtapes] = useState<Etape[]>([]);
  const [rappel, setRappel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** Aperçu avant écriture, pour les thèmes historiques qui le proposent. */
  const [apercu, setApercu] = useState<{ theme: string; data: ImportApercu; fichier: File } | null>(null);
  /** Rapport d'import du parc : rattaché vs CRÉÉ. */
  const [rapport, setRapport] = useState<{
    theme: string; crees: number; ignorees: number;
    lieux: { rattaches: number; crees: number; nomsCrees: string[] };
    erreurs: string[];
  } | null>(null);

  useEffect(() => {
    api.admin.parcImport.ordre()
      .then((r) => { setEtapes(r.etapes); setRappel(r.rappel); })
      .catch((e) => setError(e instanceof ApiException ? e.message : 'Chargement impossible'));
  }, []);

  const echoue = (e: unknown, defaut: string) =>
    setError(e instanceof ApiException ? e.message : defaut);

  async function modele(t: ThemeLigne) {
    setError(null);
    try {
      // Le dossier agent a ses propres points d'API, hors système de thèmes.
      if (t.famille === 'rh') await api.rh.telechargerModeleAgents();
      else if (t.famille === 'parc') await api.admin.parcImport.modele(t.theme);
      else await api.admin.importModele(t.theme);
    } catch (e) { echoue(e, 'Modèle indisponible'); }
  }

  async function exporter(t: ThemeLigne) {
    setError(null);
    try {
      if (t.famille === 'rh') await api.rh.telechargerAgentsExcel();
      else if (t.famille === 'parc') await api.admin.parcImport.exporter(t.theme);
      else await api.admin.exportThemeExcel(t.theme);
    } catch (e) { echoue(e, 'Export impossible'); }
  }

  async function deposer(t: ThemeLigne, fichier: File) {
    setBusy(t.theme); setError(null); setMsg(null);
    setApercu(null); setRapport(null);
    try {
      if (t.famille === 'rh') {
        // L'import RH écrit directement et renvoie SON rapport : dossiers créés,
        // mis à jour, comptes ouverts, invitations envoyées. On l'affiche tel
        // quel plutôt que de le forcer dans le format du parc.
        const r = await api.rh.importerAgentsExcel(fichier);
        setMsg(
          `${r.crees} dossier${r.crees > 1 ? 's' : ''} créé${r.crees > 1 ? 's' : ''}`
          + `, ${r.majs} mis à jour, ${r.comptesCrees} compte${r.comptesCrees > 1 ? 's' : ''} ouvert`
          + `${r.comptesCrees > 1 ? 's' : ''}, ${r.invitationsEnvoyees} invitation`
          + `${r.invitationsEnvoyees > 1 ? 's' : ''} envoyée${r.invitationsEnvoyees > 1 ? 's' : ''}.`);
        if (r.erreurs.length > 0) {
          setError(r.erreurs.slice(0, 10)
            .map((e) => `Ligne ${e.ligne} : ${e.message}`).join(' · '));
        }
      } else if (t.famille === 'parc') {
        // Le parc écrit directement : son rapport distingue rattaché et créé,
        // ce qui joue le rôle de la prévisualisation.
        setRapport(await api.admin.parcImport.importer(t.theme, fichier));
      } else {
        // Les thèmes historiques passent par un dry-run : on montre les erreurs
        // AVANT d'écrire. On ne casse pas ce garde-fou.
        setApercu({ theme: t.theme, data: await api.admin.importPreview(t.theme, fichier), fichier });
      }
    } catch (e) { echoue(e, 'Lecture du fichier impossible'); }
    finally { setBusy(null); }
  }

  async function valider() {
    if (!apercu) return;
    setBusy(apercu.theme); setError(null);
    try {
      const r = await api.admin.importCommit(apercu.theme, apercu.fichier);
      setMsg(`${r.inseres} ligne${r.inseres > 1 ? 's' : ''} enregistrée${r.inseres > 1 ? 's' : ''}.`);
      setApercu(null);
    } catch (e) { echoue(e, 'Enregistrement impossible'); }
    finally { setBusy(null); }
  }

  const variante = (c: string) =>
    (c === 'success' ? 'success' : c === 'warning' ? 'warning' : 'neutral') as
      'success' | 'warning' | 'neutral';

  return (
    <div className="min-h-screen text-ink">
      <ModuleHeader accent="grc" icon={Database} eyebrow="Administration"
        title="Données — import & export" />

      <main id="main-content" className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {msg && <Alert variant="success" icon={CheckCircle2}>{msg}</Alert>}

        <Alert variant="info" icon={Info}>
          <div className="space-y-1">
            <p><strong>{rappel}</strong></p>
            <p className="text-xs">
              Le modèle a les mêmes colonnes que l’export : vous pouvez exporter,
              corriger en masse dans Excel, puis réimporter.
            </p>
          </div>
        </Alert>

        {/* ── L'aperçu avant écriture (thèmes historiques) ─────────────────── */}
        {apercu && (
          <Card variant="glass" padded>
            <h3 className="mb-2 text-sm font-semibold text-ink">
              Vérification — rien n’est encore enregistré
            </h3>
            <p className="text-sm text-ink">
              {apercu.data.valides} ligne{apercu.data.valides > 1 ? 's' : ''} valide
              {apercu.data.valides > 1 ? 's' : ''} sur {apercu.data.total}
              {apercu.data.invalides > 0 && (
                <> · <strong className="text-urgent-fg">{apercu.data.invalides} à corriger</strong></>
              )}
            </p>
            {apercu.data.erreurs?.length > 0 && (
              <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-2xs text-ink-muted">
                {apercu.data.erreurs.map((e, i) => (
                  <li key={i}>Ligne {e.ligne} : {e.erreurs.join(' · ')}</li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => void valider()}
                disabled={busy !== null || apercu.data.valides === 0}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  : <>Enregistrer les {apercu.data.valides} lignes valides</>}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setApercu(null)}>Annuler</Button>
            </div>
          </Card>
        )}

        {/* ── Le rapport du parc : rattaché vs CRÉÉ ────────────────────────── */}
        {rapport && (
          <Alert variant={rapport.lieux.crees > 0 || rapport.erreurs.length > 0
            ? 'warning' : 'success'}>
            <div className="space-y-1">
              <p>
                <strong>{rapport.crees}</strong> ligne{rapport.crees > 1 ? 's' : ''} importée
                {rapport.crees > 1 ? 's' : ''}
                {rapport.ignorees > 0 && <> · {rapport.ignorees} ignorée{rapport.ignorees > 1 ? 's' : ''}</>}
              </p>
              <p className="text-xs">
                Bâtiments : <strong>{rapport.lieux.rattaches}</strong> rattaché
                {rapport.lieux.rattaches > 1 ? 's' : ''}
                {rapport.lieux.crees > 0 && (
                  <> · <strong className="text-urgent-fg">
                    {rapport.lieux.crees} CRÉÉ{rapport.lieux.crees > 1 ? 'S' : ''}
                  </strong></>
                )}
              </p>
              {/* Les noms créés, nommés : aucun code ne peut décider que « Salle
                  des fêtes » et « Salle polyvalente » sont le même bâtiment. */}
              {rapport.lieux.nomsCrees.length > 0 && (
                <p className="text-xs text-ink-muted">
                  <AlertTriangle className="mr-1 inline h-3 w-3" aria-hidden="true" />
                  Créés : {rapport.lieux.nomsCrees.map((n) => `« ${n} »`).join(', ')}.
                  Vérifiez qu’il ne s’agit pas d’un bâtiment déjà enregistré sous un
                  autre nom.
                </p>
              )}
              {rapport.erreurs.length > 0 && (
                <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-4 text-2xs">
                  {rapport.erreurs.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              )}
            </div>
          </Alert>
        )}

        {etapes.map((e) => (
          <Card key={e.numero} variant="glass" padded>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Badge variant={variante(e.couleur)} size="sm">Étape {e.numero}</Badge>
              <h2 className="text-sm font-semibold text-ink">{e.titre}</h2>
            </div>
            <p className="mb-3 text-xs text-ink-muted">{e.sousTitre}</p>

            <ul className="divide-y divide-border">
              {e.themes.map((t) => (
                <li key={t.theme} className="flex flex-wrap items-start gap-2 py-3">
                  <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle"
                    aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink">{t.titre}</p>
                    <p className="text-2xs text-ink-subtle">{t.desc}</p>
                    <p className="text-2xs text-ink-muted">
                      {t.dependances.length === 0
                        ? 'Aucune dépendance.'
                        : <>A besoin de : {t.dependances.join(', ')}.</>}
                    </p>
                  </div>

                  <span className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="glass" onClick={() => void modele(t)}>
                      <Download className="h-4 w-4" aria-hidden="true" /> Modèle
                    </Button>
                    <Button size="sm" variant="glass" onClick={() => void exporter(t)}>
                      <Download className="h-4 w-4" aria-hidden="true" /> Exporter
                    </Button>
                    <label>
                      <span className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-r-md border border-border-strong/60 bg-surface/30 px-3 text-xs text-ink hover:bg-surface/50">
                        <Upload className="h-4 w-4" aria-hidden="true" />
                        {busy === t.theme ? 'Lecture…' : 'Importer'}
                      </span>
                      <input type="file" className="hidden" accept=".xlsx,.xls"
                        disabled={busy !== null}
                        onChange={(ev) => {
                          const f = ev.target.files?.[0];
                          if (f) void deposer(t, f);
                          ev.target.value = '';
                        }} />
                    </label>
                  </span>
                </li>
              ))}
            </ul>

            {e.numero < etapes[etapes.length - 1]!.numero && (
              <p className="mt-3 flex items-center gap-1 text-2xs text-ink-subtle">
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
                Une fois cette étape faite, retéléchargez les modèles suivants :
                ils proposeront ce que vous venez de créer.
              </p>
            )}
          </Card>
        ))}
      </main>
    </div>
  );
}
